import { PLAN_IDS_BY_MODULE } from "../data/subscriptionPlans";
import { isLocal } from "../config/env";

const PRODUCT_ID_ALIASES = {
  regulator: "policyplan",
};

const toLegacyState = (entry = {}) => {
  if (!entry) {
    return { hasAccess: false, status: "none", label: "Not Available", planId: null };
  }
  return {
    hasAccess: Boolean(entry.has_access),
    visible: entry.visible !== false,
    status: entry.has_access ? "active" : "none",
    label: entry.cta_label || (entry.has_access ? "Enter" : "Not Available"),
    destination: entry.destination || null,
    ctaLabel: entry.cta_label || "Open",
    planId: null,
  };
};

const isAmptransEmail = (currentUser = {}) =>
  String(currentUser?.email || "").trim().toLowerCase().endsWith("@amptrans.net");

const gridPlanState = (currentUser = {}) => {
  const allowed = isLocal && isAmptransEmail(currentUser);
  return {
    hasAccess: allowed,
    visible: allowed,
    status: allowed ? "dev" : "none",
    label: allowed ? "Enter" : "Not Available",
    destination: allowed ? "/utility/select" : null,
    ctaLabel: allowed ? "Enter" : "Not available",
    planId: null,
  };
};

const isScaqmdAccount = (currentUser = {}) => {
  const email = String(currentUser?.email || "").trim().toLowerCase();
  const domain = email.split("@")[1] || "";
  return (
    currentUser?.signup_channel === "southcoast"
    || domain === "aqmd.gov"
    || domain === "scaqmd.gov"
    || domain.endsWith(".aqmd.gov")
    || domain.endsWith(".scaqmd.gov")
  );
};

const deriveFallbackState = (currentUser = {}) => {
  const subscriptionType = currentUser?.subscription_type;
  const isActive = currentUser?.subscription_status === "active";
  const licensed = Boolean(
    isActive && (
      (subscriptionType && PLAN_IDS_BY_MODULE.fleet.has(subscriptionType))
      || (subscriptionType && PLAN_IDS_BY_MODULE.charger.has(subscriptionType))
    )
  );
  const fleetAccess = licensed;
  const chargerAccess = licensed;
  const datahubAccess = Boolean(currentUser?.has_datahub_access);
  const policyAccess = Boolean(
    currentUser?.has_policy_access
    || currentUser?.has_regulator_access
    || isScaqmdAccount(currentUser)
  );
  const internalOpsAccess = Boolean(currentUser?.has_internal_ops_access);

  return {
    efleetplan: {
      hasAccess: fleetAccess,
      visible: true,
      status: fleetAccess ? "active" : "none",
      label: fleetAccess ? "Enter" : "View plans",
      destination: fleetAccess ? "/fleet/dashboard" : "/subscription?module=fleet",
      ctaLabel: fleetAccess ? "Enter" : "View plans",
      planId: subscriptionType || null,
    },
    chargerplan: {
      hasAccess: chargerAccess,
      visible: true,
      status: chargerAccess ? "active" : "none",
      label: chargerAccess ? "Enter" : "Request demo",
      destination: chargerAccess ? "/charger/dashboard" : "/contact",
      ctaLabel: chargerAccess ? "Enter" : "Request demo",
      planId: null,
    },
    gridplan: gridPlanState(currentUser),
    policyplan: {
      hasAccess: policyAccess,
      visible: policyAccess,
      status: policyAccess ? "active" : "none",
      label: policyAccess ? "Enter" : "Not Available",
      destination: policyAccess ? "/regulator/map" : null,
      ctaLabel: policyAccess ? "Enter" : "Not available",
      planId: null,
    },
    datahub: {
      hasAccess: datahubAccess,
      visible: datahubAccess,
      status: datahubAccess ? "active" : "none",
      label: datahubAccess ? "Enter" : "Not Available",
      destination: datahubAccess ? "/data" : null,
      ctaLabel:       datahubAccess ? "Enter" : "Not available",
      planId: null,
    },
    internalops: {
      hasAccess: internalOpsAccess,
      visible: datahubAccess && internalOpsAccess,
      status: internalOpsAccess ? "active" : "none",
      label: internalOpsAccess ? "Enter" : "Not Available",
      destination: internalOpsAccess ? "/internal" : null,
      ctaLabel: internalOpsAccess ? "Enter" : "Not available",
      planId: null,
    },
  };
};

export const deriveProductState = (currentUser = {}) => {
  if (isLocal && !currentUser?.__devSimulateView) {
    const scaqmd = isScaqmdAccount(currentUser);
    const staff = Boolean(currentUser?.has_datahub_access);
    const internalOps = staff && Boolean(currentUser?.has_internal_ops_access);
    const hidden = {
      hasAccess: false,
      visible: false,
      status: "none",
      label: "Not Available",
      destination: null,
      ctaLabel: "Not available",
      planId: null,
    };
    const devState = {
      hasAccess: true,
      visible: true,
      status: "dev",
      label: "Dev Access",
      destination: "/home",
      ctaLabel: "Enter",
      planId: currentUser?.subscription_type || null,
    };
    const policy = scaqmd ? { ...devState, destination: "/regulator/map" } : hidden;
    return {
      efleetplan: { ...devState, destination: "/fleet/dashboard" },
      chargerplan: { ...devState, destination: "/charger/dashboard" },
      gridplan: gridPlanState(currentUser),
      policyplan: policy,
      datahub: staff ? { ...devState, destination: "/data" } : hidden,
      regulator: policy,
      internalops: internalOps ? { ...devState, destination: "/internal" } : hidden,
    };
  }

  const entitlements = currentUser?.entitlements || null;
  if (!entitlements || currentUser?.__devSimulateView) {
    const fallback = deriveFallbackState(currentUser);
    return {
      ...fallback,
      regulator: fallback.policyplan,
    };
  }

  const mapped = {
    efleetplan: toLegacyState(entitlements.efleetplan),
    chargerplan: toLegacyState(entitlements.chargerplan),
    gridplan: toLegacyState(entitlements.gridplan),
    policyplan: toLegacyState(entitlements.policyplan),
    datahub: toLegacyState(entitlements.datahub),
    internalops: toLegacyState(entitlements.internalops),
  };
  mapped.regulator = mapped.policyplan;
  return mapped;
};

export const hasProductAccess = (currentUser, productId) => {
  const state = deriveProductState(currentUser)[PRODUCT_ID_ALIASES[productId] || productId];
  return Boolean(state?.hasAccess);
};

